import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { fmtInt, fmtPct, fmtEur, fmtNum } from '@/lib/format';

// Transformation-rate colour scale (semantic tokens from the design system).
export const transfoColor = (t) => {
  if (t >= 0.85) return '#0E9F6E'; // success
  if (t >= 0.70) return '#0B7A75'; // brand teal
  if (t >= 0.55) return '#F4A13A'; // warning
  return '#D64545';                // danger
};

export const CityMap = ({ cities = [], height = 540, onSelect, selectedCity }) => {
  const maxBs = Math.max(1, ...cities.map((c) => c.bs_reel || 0));
  const radiusFor = (bs) => 6 + Math.sqrt((bs || 0) / maxBs) * 28;

  return (
    <div className="relative overflow-hidden rounded-2xl border" style={{ height }} data-testid="city-map">
      <MapContainer
        center={[46.6, 2.4]}
        zoom={6}
        scrollWheelZoom
        style={{ height: '100%', width: '100%', background: 'var(--tenant-accent-050)' }}
        preferCanvas
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; OpenStreetMap, &copy; CARTO'
        />
        {cities.map((c) => {
          const active = selectedCity === c.city;
          const col = transfoColor(c.taux_transfo);
          return (
            <CircleMarker
              key={c.city}
              center={[c.lat, c.lng]}
              radius={radiusFor(c.bs_reel)}
              pathOptions={{
                color: col,
                fillColor: col,
                fillOpacity: active ? 0.85 : 0.5,
                weight: active ? 3 : 1.4,
              }}
              eventHandlers={{ click: () => onSelect?.(c) }}
            >
              <Tooltip direction="top" offset={[0, -4]}>
                <span style={{ fontWeight: 600 }}>{c.city}</span> — {fmtInt(c.bs_reel)} BS · {fmtPct(c.taux_transfo)}
              </Tooltip>
              <Popup>
                <div style={{ minWidth: 190 }}>
                  <p className="break-words" style={{ margin: 0, fontWeight: 700, fontSize: 13 }}>{c.city}</p>
                  <p style={{ margin: '2px 0 8px', fontSize: 11, color: '#5B6472' }}>
                    {fmtInt(c.nb_spots)} spot(s) · {fmtInt(c.jours_actifs)} jours actifs
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12 }}>
                    <div><b>{fmtInt(c.bs_reel)}</b><br /><span style={{ color: '#5B6472' }}>BS validés</span></div>
                    <div><b>{fmtPct(c.taux_transfo)}</b><br /><span style={{ color: '#5B6472' }}>Transfo.</span></div>
                    <div><b>{fmtEur(c.don_moyen)}</b><br /><span style={{ color: '#5B6472' }}>Don moyen</span></div>
                    <div><b>{fmtNum(c.age_moyen, 1)}</b><br /><span style={{ color: '#5B6472' }}>Âge moyen</span></div>
                  </div>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>

      {/* Legend */}
      <div className="pointer-events-none absolute bottom-3 left-3 z-[400] rounded-xl border bg-card/90 px-3 py-2 text-[11px] backdrop-blur" data-testid="city-map-legend">
        <p className="mb-1 font-semibold text-foreground">Taux de transformation</p>
        <div className="flex items-center gap-2">
          {[['#0E9F6E', '≥85%'], ['#0B7A75', '≥70%'], ['#F4A13A', '≥55%'], ['#D64545', '<55%']].map(([c, l]) => (
            <span key={l} className="flex items-center gap-1 text-muted-foreground">
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: c }} />{l}
            </span>
          ))}
        </div>
        <p className="mt-1 text-muted-foreground">Taille du cercle = volume de BS validés</p>
      </div>
    </div>
  );
};
